import type { Tournament, TournamentSet } from "../../shared/types";
import type { CharacterColor } from "../components/StockIcon";
import { groupByPhase, setGroupKey } from "../../shared/setGroups";
import { isPlausibleStartMs } from "../../shared/tournamentDate";
import { entrantInfo } from "./characterColors";

export interface EntrantSummary {
  name: string;
  characters: CharacterColor[];
  wins: number;
  losses: number;
  setsPlayed: number;
}

/** One bracket's worth of entrants (e.g. main bracket vs. amateur). */
export interface EventEntrants {
  /** Bracket heading from `groupByPhase`, e.g. `"Silver Bracket"`. */
  eventName: string;
  entrants: EntrantSummary[];
}

export interface CharacterUsage {
  name: string;
  color: string;
  count: number;
}

export interface StageUsage {
  stage: string;
  count: number;
}

export interface TournamentTotals {
  sets: number;
  games: number;
  playMs: number;
  /** Distinct (event, phase) pairs, not just events. */
  brackets: number;
  firstMs: number;
  lastMs: number;
}

/** Return the color with the highest count (ties → first inserted). */
function pickMostCommonColor(counts: Map<string, number>): string {
  let best = "Default";
  let bestCount = 0;
  for (const [color, count] of counts) {
    if (count > bestCount) {
      best = color;
      bestCount = count;
    }
  }
  return best;
}

interface EntrantAgg {
  name: string;
  wins: number;
  losses: number;
  setsPlayed: number;
  charOrder: string[];
  charColor: Map<string, string>;
}

/**
 * Aggregate the entrants within a single bracket: the characters they
 * played (first-seen color via `entrantInfo`) and their win–loss record.
 * Records cover only sets we have replays for - not official start.gg placement.
 * Decided by comparing the player's finalScore slot to the opposing slot (the
 * same lookup `SetCard` uses); ties/undecided sets count toward setsPlayed only.
 * Doubles: each player on a side shares the side's slot.
 */
function aggregateEntrants(sets: TournamentSet[]): EntrantSummary[] {
  const map = new Map<string, EntrantAgg>();
  const get = (name: string): EntrantAgg => {
    let a = map.get(name);
    if (!a) {
      a = { name, wins: 0, losses: 0, setsPlayed: 0, charOrder: [], charColor: new Map() };
      map.set(name, a);
    }
    return a;
  };

  for (const set of sets) {
    const slots = set.context.finalScore.slots;
    const sides = [set.context.players.entrant1, set.context.players.entrant2];
    for (const side of sides) {
      for (const player of side) {
        const slot = slots.find((s) => s.displayNames.includes(player.name));
        const other = slots.find((s) => s !== slot);
        const a = get(player.name);
        a.setsPlayed++;
        if (slot && other) {
          if (slot.score > other.score) a.wins++;
          else if (slot.score < other.score) a.losses++;
        }
        for (const c of entrantInfo(slot, set.gameSummaries).characters) {
          if (!a.charColor.has(c.name)) {
            a.charOrder.push(c.name);
            a.charColor.set(c.name, c.color ?? "Default");
          }
        }
      }
    }
  }

  return Array.from(map.values())
    .map((a) => ({
      name: a.name,
      characters: a.charOrder.map((name) => ({ name, color: a.charColor.get(name) })),
      wins: a.wins,
      losses: a.losses,
      setsPlayed: a.setsPlayed,
    }))
    .sort((x, y) => y.wins - x.wins || x.losses - y.losses || x.name.localeCompare(y.name));
}

/**
 * Aggregate entrants per bracket, so main and amateur don't share one W–L
 * tally. Ordered by entrant count (largest bracket first), then name.
 */
export function buildEntrants(t: Tournament): EventEntrants[] {
  return groupByPhase(t.sets, (s) => s.context)
    .map((g) => ({ eventName: g.label, entrants: aggregateEntrants(g.items) }))
    .sort(
      (a, b) => b.entrants.length - a.entrants.length || a.eventName.localeCompare(b.eventName),
    );
}

/** Count of distinct entrant display names across all of a tournament's events. */
export function countEntrants(t: Tournament): number {
  const names = new Set<string>();
  for (const s of t.sets) {
    for (const p of s.context.players.entrant1) names.add(p.name);
    for (const p of s.context.players.entrant2) names.add(p.name);
  }
  return names.size;
}

/** Count character appearances across every recorded game, most-played first. */
export function buildCharacterUsage(t: Tournament): CharacterUsage[] {
  const counts = new Map<string, number>();
  const colorCounts = new Map<string, Map<string, number>>();
  for (const set of t.sets) {
    for (const g of set.gameSummaries) {
      for (const p of g.players) {
        counts.set(p.characterName, (counts.get(p.characterName) ?? 0) + 1);
        let cc = colorCounts.get(p.characterName);
        if (!cc) {
          cc = new Map();
          colorCounts.set(p.characterName, cc);
        }
        cc.set(p.characterColor, (cc.get(p.characterColor) ?? 0) + 1);
      }
    }
  }
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, color: pickMostCommonColor(colorCounts.get(name)!), count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/**
 * Like `buildCharacterUsage`, but normalized by player: each character's count
 * is the number of distinct players (by display name) who played it at least
 * once, so a character isn't inflated by a few players grinding many games.
 */
export function buildCharacterPlayerUsage(t: Tournament): CharacterUsage[] {
  const players = new Map<string, Set<string>>();
  const colorCounts = new Map<string, Map<string, number>>();
  for (const set of t.sets) {
    for (const g of set.gameSummaries) {
      for (const p of g.players) {
        let ps = players.get(p.characterName);
        if (!ps) {
          ps = new Set();
          players.set(p.characterName, ps);
        }
        ps.add(p.displayName);
        let cc = colorCounts.get(p.characterName);
        if (!cc) {
          cc = new Map();
          colorCounts.set(p.characterName, cc);
        }
        cc.set(p.characterColor, (cc.get(p.characterColor) ?? 0) + 1);
      }
    }
  }
  return Array.from(players.entries())
    .map(([name, set]) => ({
      name,
      color: pickMostCommonColor(colorCounts.get(name)!),
      count: set.size,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/** Count stage appearances across every recorded game, most-played first. */
export function buildStageUsage(t: Tournament): StageUsage[] {
  const counts = new Map<string, number>();
  for (const set of t.sets) {
    for (const g of set.gameSummaries) {
      counts.set(g.stageName, (counts.get(g.stageName) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([stage, count]) => ({ stage, count }))
    .sort((a, b) => b.count - a.count || a.stage.localeCompare(b.stage));
}

/** Roll up set/game counts, total play time, distinct brackets, and date range. */
export function buildTournamentTotals(t: Tournament): TournamentTotals {
  let games = 0;
  let playMs = 0;
  let firstMs = Infinity;
  let lastMs = -Infinity;
  const brackets = new Set<string>();
  for (const set of t.sets) {
    games += set.gameSummaries.length;
    for (const g of set.gameSummaries) playMs += g.durationSeconds * 1000;
    brackets.add(setGroupKey(set.context.startgg).key);
    const ms = set.context.startMs;
    // Skip future-dated outliers (a capture station with a mis-set clock) so
    // they don't stretch the reported date range.
    if (!isPlausibleStartMs(ms)) continue;
    if (ms < firstMs) firstMs = ms;
    if (ms > lastMs) lastMs = ms;
  }
  return {
    sets: t.sets.length,
    games,
    playMs,
    brackets: brackets.size,
    firstMs: isFinite(firstMs) ? firstMs : 0,
    lastMs: isFinite(lastMs) ? lastMs : 0,
  };
}
